import firebaseConfigs from 'firebaseConfigs.json';
import history from 'history.js';

// The Nutivate-owned Firebase project's web settings for this build's environment, or undefined when none is set
// yet, which turns push notifications off (nutivate-web#49). The push service worker gets the same settings
// through scripts/write-firebase-config.js.
export const firebaseConfig = firebaseConfigs[process.env.REACT_APP_ENV || 'development'] || undefined;

// iPads ask for the computer site and report themselves as a Mac; a Mac has no touch screen.
export const isIOSDevice = ({ ua, platform, maxTouchPoints }) =>
  /iPad|iPhone|iPod/.test(ua) || (platform === 'MacIntel' && maxTouchPoints > 1);

export const isIOS = () =>
  isIOSDevice({ ua: navigator.userAgent, platform: navigator.platform, maxTouchPoints: navigator.maxTouchPoints });

// Opened from the Home Screen. iPhones only allow web push there.
export const isStandalone = () =>
  window.navigator.standalone === true ||
  (!!window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);

export const isPushSupported = () =>
  !!firebaseConfig && 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;

export const pushPermission = () => (isPushSupported() ? Notification.permission : 'unsupported');

// The push service worker, at the script and scope Firebase uses by default, so Firebase reuses this registration.
const PUSH_WORKER = '/firebase-messaging-sw.js';
const PUSH_SCOPE = '/firebase-cloud-messaging-push-scope';
// How long to wait for a new push worker to start before trying anyway.
const PUSH_WORKER_WAIT_MS = 10 * 1000;

// Registers the push worker and waits until it is running. Firebase 9 asks for the device token as soon as it has
// registered the worker, and on a browser's first visit the worker isn't running yet, so the token request failed
// ("no active Service Worker") and the device was only registered on the next page load.
export const pushWorkerReady = () =>
  navigator.serviceWorker.register(PUSH_WORKER, { scope: PUSH_SCOPE }).then((registration) => {
    const worker = registration.installing || registration.waiting;
    if (registration.active || !worker) return registration;
    return new Promise((resolve) => {
      const timer = setTimeout(() => resolve(registration), PUSH_WORKER_WAIT_MS);
      worker.addEventListener('statechange', () => {
        if (worker.state !== 'activated') return;
        clearTimeout(timer);
        resolve(registration);
      });
    });
  });

// Firebase asks for permission when it has none yet, and iPhones only allow asking after a tap, so the device
// token is only registered once the member has allowed notifications (the in-chat invite asks).
export const whenPushAllowed = (setFirebaseToken) => () => {
  if (!setFirebaseToken || pushPermission() !== 'granted') return;
  // A failed worker registration is left to Firebase, which reports it as before.
  pushWorkerReady()
    .catch(() => {})
    .then(() => setFirebaseToken());
};

// A tapped notification focuses an open tab of the site and asks it to show the chat.
export const startPushClickListener = () => {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data && event.data.type === 'nutivate:open-chat' && window.location.pathname !== '/chat') {
      history.push('/chat');
    }
  });
};
